import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as auth from '../../../store/actions/auth.action';
import { useState } from "react";
import { getLocalStorageValue, setLocalStorageValue } from "utils/handler";
import { useHistory } from "react-router";
import FaqModal from "./faqModal";
import { PopupModal } from "react-calendly";
import { RootState } from "store/reducers";
import { useSelector } from "react-redux";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';

interface FormInputType {
    fullname: string;
    email: string;
    phone: string;
    domain: string;
    budget: number;
    comments?: string;
    terms: boolean;

    // Honeypot field
    website?: string;
}

const Form: React.FC<{ showFaqModal?: boolean, setShowFaqModal?: (x: boolean) => void }> = ({ showFaqModal = false, setShowFaqModal = () => {} }): JSX.Element => {

    const { localCountryStr } = useSelector((state: RootState) => state.mainReducer);

    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showCalendlyModal, setShowCalendlyModal] = useState(false);
    const [calendlyAgent, setCalendlyAgent] = useState("");
    const [calendlyName, setCalendlyName] = useState("");
    const [calendlyEmail, setCalendlyEmail] = useState("");


    const [leadID, setLeadID] = useState(() => {
        return getLocalStorageValue("tempLeadID");
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        control,
        trigger,
    } = useForm<FormInputType>({
        mode: "onSubmit",
        defaultValues: {
            domain: getLocalStorageValue("domainName")
        }
    });

    // console.log("**********", { errors, isValid })

    const makePayment = async (leadUID?: string, delay?: number) => {
        if (!leadUID) {
            leadUID = leadID
        }
        const link = await auth.getPaymentLink(leadUID);
        console.log({ link })
        if (link === "calendly") {
            history.push("/calendly");
        } else if (link) {
            if (delay) {
                await new Promise((resolve) => setTimeout(resolve, delay));
                window.location.href = link;
            } else {
                window.location.href = link;
            }
        }
    }

    const onSubmit: SubmitHandler<FormInputType> = async (data) => {
        console.log("****************Form Data:", data);
        // Honeypot triggered
        if (data.website) {
            console.warn("Bot detected for this submission:", data);
            // return;
        }

        setIsSubmitting(true);
        try {
            const newLeadRes = await auth.createLeadWithoutSignup({
                name: data.fullname,
                email: data.email,
                phone: data.phone,
                domainName: data.domain,
                offerAmount: data.budget.toString(),
                comments: data.comments
            });

            if (newLeadRes?.status === 200 && newLeadRes?.data !== "BAD LEAD") {
                setLocalStorageValue("tempLeadID", newLeadRes.data);
                setLeadID(newLeadRes.data);
                await makePayment(newLeadRes.data)
            } else if (newLeadRes?.data === "BAD LEAD" || newLeadRes?.status === 203) {
                reset();
            } else if (newLeadRes?.status === 201 && newLeadRes?.data) {
                setLocalStorageValue("tempLeadID", newLeadRes.data);
                setLeadID(newLeadRes.data);
                reset();
                const calendlyRes = await auth.getCalendly(newLeadRes.data);
                console.log({ calendlyRes })
                if (calendlyRes?.agent) {
                    setCalendlyAgent(calendlyRes.agent);
                    setCalendlyName(calendlyRes.user?.name || "");
                    setCalendlyEmail(calendlyRes.user?.email || "");
                    setShowCalendlyModal(true);
                }
            }
        } catch (error) {
            console.error("Submission error:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="rewamp-form">
            <form className="form" onSubmit={handleSubmit(onSubmit)}>

                <div className="form-group">
                    <input type="text" {...register("fullname", {
                        required: "Full name is required",
                        pattern: {
                            value: /^[A-Za-z]{2,}(?:\s[A-Za-z]{2,})+$/,
                            message: "Please enter your first and last name (at least 2 letters each)"
                        }
                    })} placeholder="Full Name*" />
                    {errors.fullname && <span className="field-error">{errors.fullname.message}</span>}
                </div>

                <div className="form-group">
                    <input type="email" {...register("email", {
                        required: "Email is required",
                        pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: "Invalid email"
                        }
                    })} placeholder="Email Address*" />
                    {errors.email && <span className="field-error">{errors.email.message}</span>}
                </div>

                <div className="form-group new-phone-input">
                    <Controller
                        name="phone"
                        control={control}
                        rules={{
                            required: "Phone number is required",
                            validate: (value) =>
                                isValidPhoneNumber(value || "") || "Invalid phone number"
                        }}
                        render={({ field }) => (
                            <PhoneInput
                                name={field.name}
                                international
                                defaultCountry={localCountryStr}
                                placeholder="Phone Number*"
                                value={field.value}
                                onChange={field.onChange}
                                className="form-control"
                            />
                        )}
                    />
                    {errors.phone && <span className="field-error">{errors.phone.message}</span>}
                </div>

                <div
                    style={{
                        position: "absolute",
                        left: "-9999px",
                        opacity: 0,
                        pointerEvents: "none"
                    }}
                    aria-hidden="true"
                >
                    <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        {...register("website")}
                    />
                </div>

                <div className="form-group">
                    <input type="text" {...register("domain", { required: "Domain is required" })} placeholder="Domain Name*" />
                    {errors.domain && <span className="field-error">{errors.domain.message}</span>}
                </div>

                <div className="form-group">
                    <input type="text" {...register("budget", {
                        required: "Budget is required",
                        valueAsNumber: true,
                        validate: {
                            // 1. Only digits (Manual check since pattern hates numbers)
                            onlyNumbers: (v) => /^[0-9]+(\.[0-9]+)?$/.test(v?.toString()) || "Only whole numbers are allowed",

                            // 2. Your specific floor logic
                            // floor: (v) => (v >= 300) || "Budget must be at least $300",

                            // 3. Your specific ceiling logic
                            troll: (v) => (v < 1000000000) || "Budget cannot exceed $1 Billion"
                        }
                    })}
                        inputMode="numeric" placeholder="Approximate Budget*" />
                    {errors.budget && <span className="field-error">{errors.budget.message}</span>}
                </div>

                <div className="form-group">
                    <textarea {...register("comments", {
                        maxLength: {
                            value: 500,
                            message: "Comments can't exceed 500 characters"
                        },
                        required: "Comments are required",
                    })} placeholder="Comments" rows={4}></textarea>
                    {errors.comments && <span className="field-error">{errors.comments.message}</span>}
                </div>

                <div className="form-group">
                    <div className="checkbox" >
                        <input type="checkbox" {...register("terms", {
                            required: "You must accept terms"
                        })} id="terms" />
                        <label htmlFor="terms">
                            By ticking this box I agree that I have read and consent to the{' '}
                            <a href="https://saw.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                            <a href="https://saw.com/terms-of-service" target="_blank" rel="noopener noreferrer">Account Terms of Service</a>.
                        </label>
                    </div>
                    {errors.terms && <span className="field-error">{errors.terms.message}</span>}
                </div>

                <button type="submit" disabled={isSubmitting}>{isSubmitting ? "Submitting..." : "Submit Request"}</button>
                {/* {leadID && !isSubmitting && <button type="button" onClick={() => makePayment()}>Get Started</button>} */}
            </form>
            {showFaqModal && <FaqModal closePopup={() => setShowFaqModal(false)} />}
            <PopupModal
                // url={"https://calendly.com/paul-9872/15min"}
                url={"https://calendly.com/" + calendlyAgent + "/15min"}
                prefill={{ name: calendlyName, email: calendlyEmail }}
                open={showCalendlyModal}
                onModalClose={() => setShowCalendlyModal(false)}
                rootElement={document.body}
            />
        </div>
    )
}

export default Form;
